import { useState } from 'react';

import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/design-system/ui/Dialog';
import { PRIVACY_NOTE_MAX, PRIVACY_REQUEST_LABEL } from '@escala/domain/privacy';
import type { PrivacyRequestKind } from '@escala/domain/types';
import { useStore } from '@/store/AppStore';

/**
 * Pedido do titular (revogar o consentimento, excluir a conta ou outro): a observação e o
 * envio, que vai para a administração da igreja. A exclusão avisa o que acontece.
 */
export default function PrivacyRequestDialog({
  kind,
  onClose,
}: {
  kind: PrivacyRequestKind;
  onClose: () => void;
}) {
  const { actions } = useStore();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const label = PRIVACY_REQUEST_LABEL[kind];

  const send = () => {
    const problem = actions.submitPrivacyRequest(kind, note);

    if (problem) return setError(problem);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[min(480px,calc(100vw-24px))]" described>
        <DialogHeader
          title={label.title}
          description="O pedido vai para a administração da igreja e para o encarregado de dados."
        />
        <DialogBody className="gap-3.5">
          {kind === 'deletion' ? (
            <Alert tone="danger" role="note">
              Seu acesso será encerrado e seus dados pessoais serão anonimizados. O histórico de escalas
              continua só em estatísticas, sem identificar você. Dados que a lei exige manter serão
              preservados pelo prazo legal.
            </Alert>
          ) : null}
          {kind === 'revoke' ? (
            <p className="m-0 text-small text-pretty text-ink-secondary">
              Sem o consentimento, a igreja deixa de tratar os dados que dependem dele — como a sua
              participação na igreja — e pode não conseguir mais escalar você.
            </p>
          ) : null}
          <label className="flex flex-col gap-1.5">
            <span className="text-control font-semibold">
              {kind === 'other' ? 'Qual é o seu pedido?' : 'Observação (opcional)'}
            </span>
            <textarea
              rows={3}
              maxLength={PRIVACY_NOTE_MAX}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setError(null);
              }}
              className="w-full min-w-0 resize-y rounded-control border border-line-input bg-white px-3 py-2.5 text-control text-ink placeholder:text-ink-placeholder"
            />
          </label>
          {error ? <Alert tone="danger">{error}</Alert> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant={kind === 'deletion' ? 'danger' : 'primary'} onClick={send}>
            Enviar pedido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
